import PaxDetails from "./PaxDetails";
import { Icon } from "@thetreep/ui";
import {
  Journey,
  Passenger,
  Price,
  Product,
  Travel,
} from "@gen/travel/v1/common_pb";
import i18n from "@libs/i18n";

type ProductsDisplayProps = {
  journey: Journey;
  travel: Travel;
  products: Product[];
  passenger: Passenger;
};

export default function ProductsDisplay({
  journey,
  products,
  passenger,
  travel,
}: ProductsDisplayProps) {
  function getPrice() {
    const price = travel.initialPrices.reduce<Price | undefined>((acc, p) => {
      // is true if every product of this price bundle belongs to the journey.
      // it can be false if the bundle is for outward/inward (duffel for example).
      // in this case, do not display the price detail for outward/inward.
      const productsBelongToJourney = p.productIds.every((id) => {
        const product = products.find((p) => p.id === id);
        if (!product) return false;
        return journey.segments.some((s) => s.id === product.segmentId);
      });
      // is true if every product of this price bundle belongs to the passenger.
      const productsBelongToPassenger = p.productIds.every((id) => {
        const product = products.find((p) => p.id === id);
        if (!product) return false;
        return product.passengerId === passenger.id;
      });
      // ignore this price bundle if it does not belong to the journey or the passenger.
      if (!productsBelongToJourney || !productsBelongToPassenger) return acc;
      if (!acc) return p.price;
      return { ...acc, amount: acc.amount + p.price!.amount };
    }, undefined);

    if (!price) return "";
    const formatter = new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: price.currency || "EUR",
    });
    return formatter.format(Number(price.amount) / 100);
  }

  return (
    <div className="flex flex-col  justify-between ">
      <div className="flex justify-between font-bold">
        <div className="flex items-center justify-center space-x-2">
          <Icon size={18} src="user_4_fill" />
          <PaxDetails pax={passenger} />
        </div>
        <div className="flex items-center font-bold text-base text-text-strong">
          {getPrice()}
        </div>
      </div>
      {products.map((product, index) => (
        <ProductDisplay key={index} journey={journey} product={product} />
      ))}
    </div>
  );
}

function ProductDisplay({ product }: { product: Product; journey: Journey }) {
  return (
    <div className="ml-6 mt-1 text-text-strong">
      <span className="">{product.code}</span>
      <p
        className="text-xs mt-1"
        dangerouslySetInnerHTML={{
          __html: product.conditions.map((c) => c.description).join(", "),
        }}
      />
    </div>
  );
}
